<template>
  <div class="profile-page">

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1>Admin Profile</h1>
        <p>Manage your admin account and profile information.</p>
      </div>

      <button
        v-if="!isEditing"
        class="btn btn-primary"
        @click="startEditing"
      >
        <i class="bx bx-edit"></i>
        Edit Profile
      </button>

      <div v-else class="header-actions">
        <button class="btn btn-secondary" @click="cancelEditing">
          Cancel
        </button>

        <button class="btn btn-primary" @click="saveProfile">
          <i class="bx bx-save"></i>
          Save Changes
        </button>
      </div>
    </div>

    <!-- Profile Layout -->
    <div class="profile-grid">

      <!-- Left Profile Card -->
      <div class="profile-card profile-summary-card">

        <div class="profile-cover"></div>

        <div class="profile-avatar-wrapper">
          <div class="profile-avatar">
            <img
              v-if="profile.image"
              :src="profile.image"
              alt="Admin"
            />

            <span v-else>
              {{ getInitials(profile.name) }}
            </span>
          </div>

          <button
            v-if="isEditing"
            class="avatar-edit"
            title="Change profile image"
            @click="changeProfileImage"
          >
            <i class="bx bx-camera"></i>
          </button>
        </div>

        <div class="profile-summary">
          <h2>{{ profile.name }}</h2>

          <p class="profile-role">
            <i class="bx bx-shield-quarter"></i>
            Administrator
          </p>

          <div class="verification-badge">
            <i class="bx bx-check-circle"></i>
            Verified Account
          </div>
        </div>

        <div class="profile-divider"></div>

        <div class="profile-info-list">

          <div class="info-item">
            <div class="info-icon">
              <i class="bx bx-envelope"></i>
            </div>

            <div>
              <span>Email</span>
              <strong>{{ profile.email || 'Not provided' }}</strong>
            </div>
          </div>

          <div class="info-item">
            <div class="info-icon">
              <i class="bx bx-phone"></i>
            </div>

            <div>
              <span>Mobile</span>
              <strong>{{ profile.mobile || 'Not provided' }}</strong>
            </div>
          </div>

          <div class="info-item">
            <div class="info-icon">
              <i class="bx bx-calendar"></i>
            </div>

            <div>
              <span>Member Since</span>
              <strong>{{ formatDate(profile.user_cre_datetime) }}</strong>
            </div>
          </div>

        </div>
      </div>

      <!-- Right Content -->
      <div class="profile-content">

        <!-- Personal Information -->
        <div class="content-card">

          <div class="card-header">
            <div>
              <h3>Personal Information</h3>
              <p>Basic information associated with your admin account.</p>
            </div>

            <i class="bx bx-user-circle card-header-icon"></i>
          </div>

          <div class="form-grid">

            <div class="form-group">
              <label>Full Name</label>

              <input
                v-if="isEditing"
                v-model="editProfile.admin_name"
                type="text"
                placeholder="Enter full name"
              />

              <div v-else class="readonly-value">
                {{ profile.name || 'Not provided' }}
              </div>
            </div>

            <div class="form-group">
              <label>Email Address</label>

              <input
                v-if="isEditing"
                v-model.trim="editProfile.admin_email"
                type="email"
                placeholder="Enter email address"
              />

              <div v-else class="readonly-value">
                {{ profile.email || 'Not provided' }}
              </div>
            </div>

            <div class="form-group">
              <label>Mobile Number</label>

              <input
                v-if="isEditing"
                v-model.trim="editProfile.mobile"
                type="tel"
                pattern="[0-9]{7,15}"
                placeholder="Enter mobile number"
              />

              <div v-else class="readonly-value">
                {{ profile.mobile || 'Not provided' }}
              </div>
            </div>

            <div class="form-group">
              <label>Signup Mode</label>

              <div class="readonly-value">
                {{ profile.signup_mode || 'Admin' }}
              </div>
            </div>

          </div>

        </div>

        <!-- Address Information -->
        <div class="content-card">

          <div class="card-header">
            <div>
              <h3>Address Information</h3>
              <p>Location information associated with your account.</p>
            </div>

            <i class="bx bx-map card-header-icon"></i>
          </div>

          <div class="form-grid">

            <div class="form-group full-width">
              <label>Address</label>

              <textarea
                v-if="isEditing"
                v-model="editProfile.address"
                rows="3"
                placeholder="Enter address"
              ></textarea>

              <div v-else class="readonly-value textarea-value">
                {{ profile.address || 'Not provided' }}
              </div>
            </div>

            <div class="form-group">
              <label>City</label>

              <select
                v-if="isEditing"
                v-model="editProfile.city"
                :disabled="!editProfile.state"
              >
                <option value="">Select city</option>
                <option
                  v-for="city in cities"
                  :key="city.id || city.city_id || city.city_name || city.name"
                  :value="city.name || city.city_name"
                >
                  {{ city.name || city.city_name }}
                </option>
              </select>

              <div v-else class="readonly-value">
                {{ profile.city || 'Not provided' }}
              </div>
            </div>

            <div class="form-group">
              <label>State</label>

              <select
                v-if="isEditing"
                v-model="editProfile.state"
                :disabled="!editProfile.country"
                @change="onStateChange"
              >
                <option value="">Select state</option>
                <option
                  v-for="state in states"
                  :key="state.id || state.state_id || state.state_name || state.name"
                  :value="state.name || state.state_name"
                >
                  {{ state.name || state.state_name }}
                </option>
              </select>

              <div v-else class="readonly-value">
                {{ profile.state || 'Not provided' }}
              </div>
            </div>

            <div class="form-group">
              <label>Country</label>

              <select
                v-if="isEditing"
                v-model="editProfile.country"
                @change="onCountryChange"
              >
                <option value="">Select country</option>
                <option
                  v-for="country in countries"
                  :key="country.id || country.country_id || country.country_name || country.name"
                  :value="country.name || country.country_name"
                >
                  {{ country.name || country.country_name }}
                </option>
              </select>

              <div v-else class="readonly-value">
                {{ profile.country || 'Not provided' }}
              </div>
            </div>

            <div class="form-group">
              <label>Pincode</label>

              <input
                v-if="isEditing"
                v-model="editProfile.pincode"
                type="text"
                placeholder="Enter pincode"
              />

              <div v-else class="readonly-value">
                {{ profile.pincode || 'Not provided' }}
              </div>
            </div>

          </div>

        </div>

        <!-- Social / Professional Links -->
        <div class="content-card">

          <div class="card-header">
            <div>
              <h3>Professional & Social Links</h3>
              <p>Links associated with your Creator Dost account.</p>
            </div>

            <i class="bx bx-link card-header-icon"></i>
          </div>

          <div class="form-grid">

            <div class="form-group">
              <label>
                <i class="bx bxl-youtube"></i>
                YouTube Channel
              </label>

              <input
                v-if="isEditing"
                v-model="editProfile.youtube_channel"
                type="url"
                placeholder="YouTube channel URL"
              />

              <div v-else class="readonly-value link-value">
                {{ profile.youtube_channel || 'Not provided' }}
              </div>
            </div>

            <div class="form-group">
              <label>
                <i class="bx bxl-instagram"></i>
                Instagram
              </label>

              <input
                v-if="isEditing"
                v-model="editProfile.insta_link"
                type="url"
                placeholder="Instagram profile URL"
              />

              <div v-else class="readonly-value link-value">
                {{ profile.insta_link || 'Not provided' }}
              </div>
            </div>

            <div class="form-group full-width">
              <label>
                <i class="bx bx-globe"></i>
                Portfolio
              </label>

              <input
                v-if="isEditing"
                v-model="editProfile.portfolio_link"
                type="url"
                placeholder="Portfolio website URL"
              />

              <div v-else class="readonly-value link-value">
                {{ profile.portfolio_link || 'Not provided' }}
              </div>
            </div>

          </div>

        </div>

        <!-- Account Information -->
        <div class="content-card">

          <div class="card-header">
            <div>
              <h3>Account Information</h3>
              <p>System information for this admin account.</p>
            </div>

            <i class="bx bx-shield card-header-icon"></i>
          </div>

          <div class="account-info-grid">

            <div class="account-info">
              <span>User ID</span>
              <strong>#{{ profile.user_id }}</strong>
            </div>

            <div class="account-info">
              <span>Account Status</span>

              <strong class="status-active">
                <span></span>
                Active
              </strong>
            </div>

            <div class="account-info">
              <span>Email Verification</span>

              <strong class="status-verified">
                <i class="bx bx-check-circle"></i>
                {{ profile.isVerified ? 'Verified' : 'Not Verified' }}
              </strong>
            </div>

            <div class="account-info">
              <span>Created On</span>
              <strong>{{ formatDate(profile.user_cre_datetime) }}</strong>
            </div>

          </div>

        </div>

      </div>
    </div>

    <!-- Change Image Modal -->
    <div
      v-if="showImageModal"
      class="modal-overlay"
      @click.self="showImageModal = false"
    >
      <div class="modal-card small-modal">

        <div class="modal-header">
          <div>
            <h3>Profile Image</h3>
            <p>Enter the image URL for your profile.</p>
          </div>

          <button
            class="close-btn"
            @click="showImageModal = false"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">

          <div class="form-group">
            <label>Image URL</label>

            <input
              v-model="editProfile.image"
              type="text"
              placeholder="https://example.com/profile.jpg"
            />
          </div>

          <div
            v-if="editProfile.image"
            class="image-preview"
          >
            <img
              :src="editProfile.image"
              alt="Profile Preview"
            />
          </div>

        </div>

        <div class="modal-footer">
          <button
            class="btn btn-secondary"
            @click="showImageModal = false"
          >
            Cancel
          </button>

          <button
            class="btn btn-primary"
            @click="showImageModal = false"
          >
            Apply Image
          </button>
        </div>

      </div>
    </div>

    <!-- Success Toast -->
    <transition name="toast">
      <div
        v-if="showSuccess"
        class="success-toast"
      >
        <i class="bx bx-check-circle"></i>

        <div>
          <strong>Profile Updated</strong>
          <span>Your profile changes have been saved.</span>
        </div>
      </div>
    </transition>

  </div>
</template><script>
import {
  getAdminProfileApi,
  updateAdminProfileApi,
} from "@/apis/adminprofileapis";

export default {
  name: "AdminProfileScreen",

  data() {
    return {
      loading: false,
      saving: false,
      isEditing: false,

      showImageModal: false,
      showSuccess: false,

      countries: [],
      states: [],
      cities: [],

      profile: {
        admin_id: null,
        admin_name: "",
        admin_email: "",
        admin_is_active: false,
        admin_cre_datetime: null,
        admin_upd_datetime: null,
        mobile: "",
        address: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        youtube_channel: "",
        insta_link: "",
        portfolio_link: "",
        image: "",
        isVerified: false,
        signup_mode: "",
      },

      editProfile: {
        admin_name: "",
        admin_email: "",
        mobile: "",
        address: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        youtube_channel: "",
        insta_link: "",
        portfolio_link: "",
        image: "",
      },
    };
  },

  mounted() {
    this.loadCountries();
    this.loadProfile();
  },

  methods: {
    /*
     * Country / State / City
     *
     * These loaders intentionally support the common response shapes used
     * by existing location APIs. If your project already has location API
     * helpers, replace only the fetch URLs below with those helpers.
     */
    async loadCountries() {
      try {
        const res = await fetch("/api/countries");
        const json = await res.json();

        const data = json?.data || json?.countries || json;
        this.countries = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Load countries error:", error);
        this.countries = [];
      }
    },

    async loadStates(countryName, preserveValue = false) {
      if (!countryName) {
        this.states = [];
        this.cities = [];
        if (!preserveValue) {
          this.editProfile.state = "";
          this.editProfile.city = "";
        }
        return;
      }

      try {
        const res = await fetch(
          `/api/states?country=${encodeURIComponent(countryName)}`
        );
        const json = await res.json();

        const data = json?.data || json?.states || json;
        this.states = Array.isArray(data) ? data : [];

        if (!preserveValue) {
          this.editProfile.state = "";
          this.editProfile.city = "";
          this.cities = [];
        }
      } catch (error) {
        console.error("Load states error:", error);
        this.states = [];
      }
    },

    async loadCities(stateName, preserveValue = false) {
      if (!stateName) {
        this.cities = [];
        if (!preserveValue) {
          this.editProfile.city = "";
        }
        return;
      }

      try {
        const res = await fetch(
          `/api/cities?state=${encodeURIComponent(stateName)}`
        );
        const json = await res.json();

        const data = json?.data || json?.cities || json;
        this.cities = Array.isArray(data) ? data : [];

        if (!preserveValue) {
          this.editProfile.city = "";
        }
      } catch (error) {
        console.error("Load cities error:", error);
        this.cities = [];
      }
    },

    async onCountryChange() {
      await this.loadStates(this.editProfile.country);
    },

    async onStateChange() {
      await this.loadCities(this.editProfile.state);
    },

    async loadProfile() {
      this.loading = true;

      try {
        const res = await getAdminProfileApi();

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.profile = {
            ...this.profile,
            ...data,
          };
        } else {
          alert(res?.success || "Unable to load profile.");
        }
      } catch (error) {
        console.error("Admin profile error:", error);
        alert(
          error?.response?.data?.success ||
          error?.message ||
          "Unable to load profile."
        );
      } finally {
        this.loading = false;
      }
    },

    async startEditing() {
      this.editProfile = {
        admin_name: this.profile.admin_name || "",
        admin_email: this.profile.admin_email || "",
        mobile: this.profile.mobile || "",
        address: this.profile.address || "",
        city: this.profile.city || "",
        state: this.profile.state || "",
        country: this.profile.country || "",
        pincode: this.profile.pincode || "",
        youtube_channel: this.profile.youtube_channel || "",
        insta_link: this.profile.insta_link || "",
        portfolio_link: this.profile.portfolio_link || "",
        image: this.profile.image || "",
      };

      this.isEditing = true;

      if (this.editProfile.country) {
        await this.loadStates(this.editProfile.country, true);
      }

      if (this.editProfile.state) {
        await this.loadCities(this.editProfile.state, true);
      }
    },

    cancelEditing() {
      this.editProfile = {
        admin_name: "",
        admin_email: "",
        mobile: "",
        address: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        youtube_channel: "",
        insta_link: "",
        portfolio_link: "",
        image: "",
      };

      this.states = [];
      this.cities = [];
      this.isEditing = false;
    },

    async saveProfile() {
      const adminName = String(
        this.editProfile.admin_name || ""
      ).trim();

      const adminEmail = String(
        this.editProfile.admin_email || ""
      ).trim();

      if (!adminName) {
        alert("Admin name is required.");
        return;
      }

      if (!adminEmail) {
        alert("Admin email is required.");
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobile = String(this.editProfile.mobile || "").trim();
      const mobilePattern = /^[0-9]{7,15}$/;
      const urlFields = [
        ["youtube_channel", "YouTube channel"],
        ["insta_link", "Instagram profile"],
        ["portfolio_link", "Portfolio"],
      ];

      if (!emailPattern.test(adminEmail)) {
        alert("Enter a valid admin email address.");
        return;
      }

      if (mobile && !mobilePattern.test(mobile)) {
        alert("Enter a valid mobile number using 7 to 15 digits.");
        return;
      }

      for (const [field, label] of urlFields) {
        const value = String(this.editProfile[field] || "").trim();

        if (!value) {
          continue;
        }

        try {
          const url = new URL(value);

          if (!["http:", "https:"].includes(url.protocol)) {
            throw new Error("Invalid protocol");
          }
        } catch (error) {
          alert(`${label} must be a valid http:// or https:// URL.`);
          return;
        }
      }

      this.saving = true;

      try {
        const payload = {
          ...this.editProfile,
          admin_name: adminName,
          admin_email: adminEmail,
          mobile: this.editProfile.mobile || "",
          address: this.editProfile.address || "",
          city: this.editProfile.city || "",
          state: this.editProfile.state || "",
          country: this.editProfile.country || "",
          pincode: this.editProfile.pincode || "",
          youtube_channel: this.editProfile.youtube_channel || "",
          insta_link: this.editProfile.insta_link || "",
          portfolio_link: this.editProfile.portfolio_link || "",
          image: this.editProfile.image || "",
        };

        const res = await updateAdminProfileApi(payload);

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.profile = {
            ...this.profile,
            ...payload,
            ...data,
          };

          this.isEditing = false;
          this.showSuccess = true;

          setTimeout(() => {
            this.showSuccess = false;
          }, 3000);
        } else {
          alert(
            res?.success ||
            res?.message ||
            "Unable to update profile."
          );
        }
      } catch (error) {
        console.error("Admin profile update error:", error);

        alert(
          error?.response?.data?.success ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update profile."
        );
      } finally {
        this.saving = false;
      }
    },

    changeProfileImage() {
      alert("Profile image is not available in Admin_tbl.");
    },

    getInitials(name) {
      if (!name) {
        return "A";
      }

      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
    },

    formatDate(date) {
      if (!date) {
        return "—";
      }

      const parsed = new Date(date);

      if (Number.isNaN(parsed.getTime())) {
        return "—";
      }

      return parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>


<style scoped>
.profile-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 26px;
  font-weight: 700;
}

.page-header p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 14px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Buttons */
.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s ease;
}

.btn i {
  font-size: 17px;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #fff;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f8fafc;
}

/* Main Grid */
.profile-grid {
  display: grid;
  grid-template-columns: 310px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

/* Left Card */
.profile-card,
.content-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}

.profile-summary-card {
  overflow: hidden;
  position: sticky;
  top: 20px;
}

.profile-cover {
  height: 105px;
  background: linear-gradient(
    135deg,
    #2563eb 0%,
    #3b82f6 50%,
    #60a5fa 100%
  );
}

.profile-avatar-wrapper {
  position: relative;
  width: 94px;
  height: 94px;
  margin: -47px auto 0;
}

.profile-avatar {
  width: 94px;
  height: 94px;
  border-radius: 50%;
  background: #eff6ff;
  border: 5px solid #fff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 30px;
  font-weight: 700;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.12);
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-edit {
  position: absolute;
  right: -2px;
  bottom: 2px;
  width: 31px;
  height: 31px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: #2563eb;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-edit i {
  font-size: 16px;
}

.profile-summary {
  text-align: center;
  padding: 15px 20px 20px;
}

.profile-summary h2 {
  margin: 0;
  color: #0f172a;
  font-size: 19px;
  font-weight: 700;
}

.profile-role {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin: 7px 0 12px;
  color: #64748b;
  font-size: 13px;
}

.profile-role i {
  font-size: 16px;
  color: #2563eb;
}

.verification-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border-radius: 20px;
  background: #ecfdf5;
  color: #059669;
  font-size: 11px;
  font-weight: 600;
}

.verification-badge i {
  font-size: 14px;
}

.profile-divider {
  height: 1px;
  background: #e2e8f0;
}

.profile-info-list {
  padding: 8px 20px 16px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 0;
}

.info-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-icon i {
  font-size: 18px;
}

.info-item span {
  display: block;
  color: #94a3b8;
  font-size: 11px;
  margin-bottom: 3px;
}

.info-item strong {
  display: block;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  word-break: break-word;
}

/* Right Content */
.profile-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.content-card {
  padding: 22px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 22px;
}

.card-header h3 {
  margin: 0;
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
}

.card-header p {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 12px;
}

.card-header-icon {
  color: #2563eb;
  font-size: 25px;
}

/* Form */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 20px;
}

.form-group {
  min-width: 0;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 7px;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
}

.form-group label i {
  font-size: 15px;
  color: #64748b;
}

.form-group input,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
  font-size: 13px;
  padding: 10px 12px;
  outline: none;
  transition: 0.2s ease;
}

.form-group input {
  height: 40px;
}

.form-group textarea {
  resize: vertical;
  min-height: 85px;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: #94a3b8;
}

.readonly-value {
  min-height: 40px;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  color: #334155;
  font-size: 13px;
  word-break: break-word;
}

.textarea-value {
  min-height: 70px;
  white-space: pre-line;
}

.link-value {
  color: #2563eb;
}

/* Account Information */
.account-info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.account-info {
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 9px;
}

.account-info span {
  display: block;
  color: #94a3b8;
  font-size: 11px;
  margin-bottom: 7px;
}

.account-info strong {
  color: #334155;
  font-size: 13px;
}

.status-active,
.status-verified {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.status-active {
  color: #059669 !important;
}

.status-active span {
  width: 7px;
  height: 7px;
  padding: 0;
  margin: 0;
  border-radius: 50%;
  background: #10b981;
}

.status-verified {
  color: #2563eb !important;
}

.status-verified i {
  font-size: 15px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-card {
  width: 100%;
  max-width: 500px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
  overflow: hidden;
}

.small-modal {
  max-width: 460px;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  color: #0f172a;
  font-size: 17px;
}

.modal-header p {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 12px;
}

.close-btn {
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 23px;
  padding: 0;
}

.close-btn:hover {
  color: #0f172a;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #e2e8f0;
}

.image-preview {
  margin-top: 15px;
  height: 180px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  overflow: hidden;
  background: #f8fafc;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Toast */
.success-toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1200;
  min-width: 280px;
  padding: 13px 16px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #d1fae5;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.14);
  display: flex;
  align-items: center;
  gap: 11px;
}

.success-toast > i {
  color: #10b981;
  font-size: 24px;
}

.success-toast strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  margin-bottom: 2px;
}

.success-toast span {
  color: #64748b;
  font-size: 11px;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.25s ease;
}

.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Responsive */
@media (max-width: 1100px) {
  .profile-grid {
    grid-template-columns: 270px minmax(0, 1fr);
  }

  .account-info-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 850px) {
  .profile-page {
    padding: 18px;
  }

  .profile-grid {
    grid-template-columns: 1fr;
  }

  .profile-summary-card {
    position: static;
  }

  .profile-content {
    gap: 16px;
  }
}

@media (max-width: 600px) {
  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .page-header .btn,
  .header-actions {
    width: 100%;
  }

  .header-actions .btn {
    flex: 1;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full-width {
    grid-column: auto;
  }

  .account-info-grid {
    grid-template-columns: 1fr;
  }

  .content-card {
    padding: 16px;
  }

  .success-toast {
    left: 16px;
    right: 16px;
    bottom: 16px;
    min-width: 0;
  }
}

</style>
